// ============================================================
// TMR Portal Admin — como uma taxa e sua janela temporária se leem na tela
// ============================================================
import { rateTypeLabel } from '../rateFormat';
import type { CompanyRate, RateScope, TemporaryRateState } from './contract';

// `bandLabel`, `syncSummary` and the rate's unit live in `data/rateFormat.ts`: the global template reads
// them the same way this screen does, and an operator compares one against the other. Two copies of "how a
// rate reads" would drift the first time one of them was adjusted.

/**
 * The badge key for each temporary-rate state.
 *
 * Prefixed rather than reusing the shared vocabulary, for the same reason `CONTRACT_STATUS_BADGE` is:
 * `Active` and `Ended` are generic enough to already mean something else in `STATUS_MAP`, and
 * `StatusBadge` falls back silently on an unknown key — so a collision would show one state wearing
 * another's colour and nothing would say so.
 */
export const TEMPORARY_STATE_BADGE: Record<TemporaryRateState, string> = {
  None: 'taxa_padrao',
  Scheduled: 'taxa_agendada',
  Active: 'taxa_vigente',
  Ended: 'taxa_encerrada',
};

/** What each temporary-rate state is called on screen. */
export const TEMPORARY_STATE_LABEL: Record<TemporaryRateState, string> = {
  None: 'Sem taxa temporária',
  Scheduled: 'Taxa temporária agendada',
  Active: 'Taxa temporária vigente',
  Ended: 'Taxa temporária encerrada',
};

/** What each scope is called on screen — the legacy's own words for them. */
export const SCOPE_LABEL: Record<RateScope, string> = {
  Standard: 'Taxa da Empresa',
  Launch: 'Taxa do Lançamento',
};

/** The temporary-rate filter, in the order the tab offers it. */
export const TEMPORARY_FILTER_OPTIONS: { value: TemporaryRateState; label: string }[] = [
  { value: 'Active', label: 'Vigente' },
  { value: 'Scheduled', label: 'Agendada' },
  { value: 'Ended', label: 'Encerrada' },
  { value: 'None', label: 'Sem taxa temporária' },
];

/**
 * What the rate is for, as a line the operator recognizes: the type, and for a Taxa do Lançamento the
 * product it belongs to.
 *
 * A product whose name the legacy could not give us falls back to its id rather than to nothing — the id
 * is at least something to search the legacy with.
 *
 * @param rate The rate.
 */
export function rateTitle(rate: CompanyRate): string {
  const type = rate.rateTypeName ? rateTypeLabel(rate.rateTypeName) : `Tipo ${rate.rateTypeId}`;
  if (rate.scope === 'Standard') return type;
  return `${type} · ${rate.productName ?? `Produto ${rate.productId}`}`;
}

/**
 * Whether a temporary rate applied to this rate would also reach the Empresa's products.
 *
 * Only a Taxa da Empresa cascades. This is what the modal has to say out loud, because it is the
 * difference between an exception that is charged and one that is merely recorded: the legacy credit
 * engine reads the product's rate row, not the Empresa's (`08` §8).
 *
 * @param rate The rate the operator is acting on.
 */
export function cascades(rate: CompanyRate): boolean {
  return rate.scope === 'Standard';
}

/**
 * The Launch rates a temporary rate on `rate` would carry with it — the ones the modal counts.
 *
 * Matched on rate type, credit engine and band, exactly as the server pairs them, so the number shown is
 * the number that will move. Empty for a rate that does not cascade.
 *
 * @param rate The rate the operator is acting on.
 * @param all Every rate of the Empresa.
 */
export function cascadeTargets(rate: CompanyRate, all: readonly CompanyRate[]): CompanyRate[] {
  if (!cascades(rate)) return [];

  return all.filter(
    (candidate) =>
      candidate.scope === 'Launch' &&
      candidate.rateTypeId === rate.rateTypeId &&
      candidate.creditEngineTypeId === rate.creditEngineTypeId &&
      candidate.minInstallment === rate.minInstallment &&
      candidate.maxInstallment === rate.maxInstallment,
  );
}

/**
 * Whether the legacy is out of step with what this screen is showing.
 *
 * It is the one thing a rates screen must never get wrong by omission: a rate that reads 1,5% here while
 * `taxas_produtor` still holds 3,03% is a rate nobody is being charged. `legacySynced` is false both for a
 * rate that has never replicated and for a window that flipped before its re-export ran — the wording
 * covers the second, because the first is already obvious from there being no legacy id.
 *
 * @param rate The rate.
 */
export function isAwaitingLegacy(rate: CompanyRate): boolean {
  return !rate.legacySynced;
}

/**
 * A date as the backend sends it (`YYYY-MM-DD`), formatted for reading.
 *
 * Parsed field by field rather than with `new Date(iso)`: that constructor reads a bare date as UTC
 * midnight, which in Brazil renders as the day before.
 *
 * @param iso The ISO date.
 */
export function formatDate(iso: string): string {
  const [year, month, day] = iso.split('-');
  if (!year || !month || !day) return iso;
  return `${day}/${month}/${year}`;
}

/** Today, as the backend expects a window boundary (`YYYY-MM-DD`), in the browser's own calendar. */
export function todayIso(): string {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * The products this Empresa has launch rates for, as options for the tab's Produto filter (`09` §11, N1).
 *
 * Built from the rates on screen rather than from a product catalogue, for two reasons: only a product the
 * Empresa actually has a rate for can narrow anything, and the V2 does not model `Produto` at all — the
 * name travels on each rate, read from the legacy (`08` §2).
 *
 * A launch rate whose product name did not resolve (a product deleted in the legacy) falls back to its id,
 * so it stays selectable instead of collapsing into an unnamed group with the others.
 *
 * @param rates Every rate of the Empresa, in whatever order the list came.
 */
export function launchProducts(rates: CompanyRate[]): { value: string; label: string }[] {
  const byId = new Map<string, string>();

  for (const rate of rates) {
    if (rate.scope !== 'Launch' || rate.productId === null) continue;
    const id = String(rate.productId);
    if (!byId.has(id)) byId.set(id, rate.productName ?? `Produto ${id}`);
  }

  return [...byId.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
}
